import { FC } from 'react'
import { KTIcon } from '../../../../_metronic/helpers'
import { formatNumber } from '../../../../reusable'
import { StepCommonProps } from '../../types/ads'

const BudgetPaymentStep: FC<StepCommonProps> = ({ t, adData }) => {
  const amountPerDay = parseFloat(adData?.perDayPrice ?? '0') || 0
  const duration = adData?.duration ?? 0
  const durationType = adData?.durationType ?? 0
  const totalAmount = parseFloat(String(adData?.price ?? 0)) || amountPerDay * duration
  const paymentMethod = adData?.paymentMethod ?? ''
  const paymentStatus = adData?.paymentStatus ?? ''
  const transactionNumber = adData?.transactionNumber ?? ''

  const getStatusBadge = (status: string) => {
    if (status === 'PAID') return 'adform-badge adform-badge--success'
    if (status === 'FAILED') return 'adform-badge adform-badge--danger'
    if (status === 'PENDING') return 'adform-badge adform-badge--warning'
    return 'adform-badge adform-badge--muted'
  }

  return (
    <>
      <div className='mb-6'>
        <h4 className='fw-bold fs-4 mb-1'>{t('Budget & Payment')}</h4>
        <p className='text-gray-600 fs-7 mb-0'>{t('Billing details for this advertisement')}</p>
      </div>

      {/* Billing Summary */}
      <div className='adform-billing-card'>
        <div className='adform-billing-card__header'>
          <KTIcon iconName='clipboard' className='fs-2 text-primary' />
          <span className='fw-bold fs-5'>{t('Billing Summary')}</span>
        </div>

        <div className='adform-billing-row'>
          <span className='adform-billing-row__label'>{t('Placement')}</span>
          <span className='adform-billing-row__value'>{adData?.placementName || '—'}</span>
        </div>
        <div className='adform-billing-row'>
          <span className='adform-billing-row__label'>{t('Pricing Model')}</span>
          <span className='adform-billing-row__value'>{t('Per Day')}</span>
        </div>
        <div className='adform-billing-row'>
          <span className='adform-billing-row__label'>{t('Rate Per Day')}</span>
          <span className='adform-billing-row__value'>{amountPerDay > 0 ? formatNumber(amountPerDay) : '—'}</span>
        </div>
        <div className='adform-billing-row'>
          <span className='adform-billing-row__label'>{t('Duration')}</span>
          <span className='adform-billing-row__value'>{duration > 0 ? `${duration} ${durationType}` : '—'}</span>
        </div>
        <div className='adform-billing-total'>
          <span className='fw-bold fs-4 text-gray-800'>{t('Total Amount')}</span>
          <span className='fw-bolder fs-3 text-primary'>{totalAmount > 0 ? formatNumber(totalAmount) : '—'}</span>
        </div>
      </div>

      {/* Payment Details */}
      {(paymentMethod || transactionNumber || paymentStatus) && (
        <div className='adform-billing-card mt-5'>
          <div className='adform-billing-card__header'>
            <KTIcon iconName='dollar' className='fs-2 text-success' />
            <span className='fw-bold fs-5'>{t('Payment Details')}</span>
          </div>

          {paymentMethod && (
            <div className='adform-billing-row'>
              <span className='adform-billing-row__label'>{t('Payment Method')}</span>
              <span className='adform-billing-row__value fw-bold text-capitalize'>{paymentMethod}</span>
            </div>
          )}
          {paymentStatus && (
            <div className='adform-billing-row'>
              <span className='adform-billing-row__label'>{t('Payment Status')}</span>
              <span className={getStatusBadge(paymentStatus)}>{paymentStatus}</span>
            </div>
          )}
          {transactionNumber && (
            <div className='adform-billing-row'>
              <span className='adform-billing-row__label'>{t('Transaction ID')}</span>
              <code className='adform-transaction-id'>{transactionNumber}</code>
            </div>
          )}
        </div>
      )}

      {/* No payment info */}
      {!paymentMethod && !transactionNumber && !paymentStatus && totalAmount === 0 && (
        <div className='adform-billing-empty'>
          <KTIcon iconName='information-5' className='fs-1 text-warning flex-shrink-0' />
          <div>
            <div className='fw-bold fs-6 text-warning mb-1'>{t('No Payment Information')}</div>
            <p className='text-gray-700 fs-7 mb-0'>{t('Payment details will appear here once the vendor completes the payment process.')}</p>
          </div>
        </div>
      )}
    </>
  )
}

export { BudgetPaymentStep }
